import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { removeFormField, selectAddressFormData, setFormField } from 'slices/addressFormSlice'
import { useAppDispatch } from 'store/hooks'
import { ModernizationMethodType, ModernizationValueType } from 'types/ApiResponse'
import {
  StyledCol,
  StyledColContainer,
  StyledContainer,
  StyledContinueButtonContainer,
  StyledCostsDescription,
  StyledInfoContainer,
  StyledInfoIcon,
  StyledLine,
  StyledRowContainer,
} from './ValueAddModernizationForm.styled'
import RadioGroupButtons from 'components/RadioGroupButtons/RadioGroupButtons'
import Button from 'components/Button/Button'
import Input from 'components/Input/Input'
import { ReactComponent as InfoIcon } from 'assets/images/icons/info.svg'
import ValueAddModernizationSlider from './ValueAddModernizationSlider/ValueAddModernizationSlider'
import { InputAdornment } from '@material-ui/core'
import ValueModernizationMatrixForm from './ValueModernizationMatrixForm/ValueModernizationMatrixForm'
import TextArea from 'components/TextArea/TextArea'
import { handleNumberInput } from '../../helper'

const modernizationMethods = [
  {
    key: ModernizationMethodType.SIMPLE,
    value: `modernizationMethod.${ModernizationMethodType.SIMPLE.toLowerCase()}`,
  },
  {
    key: ModernizationMethodType.DETAILED,
    value: `modernizationMethod.${ModernizationMethodType.DETAILED.toLowerCase()}`,
  },
  {
    key: ModernizationMethodType.COSTS,
    value: `modernizationMethod.${ModernizationMethodType.COSTS.toLowerCase()}`,
  },
  {
    key: ModernizationMethodType.AUTOMATIC,
    value: `modernizationMethod.${ModernizationMethodType.AUTOMATIC.toLowerCase()}`,
  },
]

const ValueAddModernizationForm = (props: any) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const addressFormData = useSelector(selectAddressFormData)
  const [showValidation, setShowValidation] = useState(false)
  const [validationErrors, setValidationErrors] = useState([])
  const { setCurrentTab } = props

  if (addressFormData.addressData?.propertyDetails?.modernizationMethod === undefined) {
    dispatch(
      setFormField({
        key: 'propertyDetails.modernizationMethod',
        value: ModernizationMethodType.SIMPLE,
      })
    )
    dispatch(
      setFormField({
        key: 'propertyDetails.modernizationValue',
        value: ModernizationValueType.NONE,
      })
    )
  }

  const validateNumericField = (currentValue, min, max, key, isFloat = false) => {
    const numericValue = isFloat
      ? parseFloat(currentValue.replace(',', '.'))
      : parseInt(currentValue)
    const numericMin = parseInt(min)
    const numericMax = parseInt(max)
    if (numericValue >= numericMax) {
      dispatch(setFormField({ key, value: `${numericMax}` }))
    } else if (numericValue <= numericMin) {
      dispatch(setFormField({ key, value: `${numericMin}` }))
    }
  }

  const validate = (name, property) => {
    if (property === null || property === undefined || property === '') {
      if (!validationErrors.includes(name)) {
        setValidationErrors((errors) => [name, ...errors])
      }
      return showValidation && true
    } else {
      if (validationErrors.includes(name)) {
        setValidationErrors((errors) => errors.filter((error) => error !== name))
      }
      return false
    }
  }

  const handleChangeModernizationMethod = (value: ModernizationMethodType) => {
    dispatch(setFormField({ key: 'propertyDetails.modernizationMethod', value }))
    setShowValidation(false)
    setValidationErrors([])
    if (value === ModernizationMethodType.AUTOMATIC) {
      dispatch(removeFormField('propertyDetails.refurbishmentYear'))
      if (validationErrors.includes('refurbishmentYear')) {
        setValidationErrors((errors) => errors.filter((error) => error !== 'refurbishmentYear'))
      }
    }
  }

  const handleChangeRefurbishmentYear = (refurbishmentYear: string, validate: boolean = false) => {
    if (refurbishmentYear.length === 0) {
      dispatch(removeFormField('propertyDetails.refurbishmentYear'))
      return
    }
    dispatch(
      setFormField({
        key: 'propertyDetails.refurbishmentYear',
        value: refurbishmentYear,
      })
    )
    if (validate) {
      validateNumericField(
        refurbishmentYear,
        addressFormData.addressData?.propertyDetails?.constructionYear,
        new Date().getFullYear(),
        'propertyDetails.refurbishmentYear'
      )
    }
  }

  const handleChangeModernizationCost = (modernizationCost: string, validate: boolean = false) => {
    if (modernizationCost.length === 0) {
      dispatch(removeFormField('propertyDetails.modernizationCost'))
      return
    }
    const regex = /^\d*(,\d{0,2})?$/
    if (regex.test(modernizationCost)) {
      dispatch(
        setFormField({
          key: 'propertyDetails.modernizationCost',
          value: modernizationCost,
        })
      )
      if (validate) {
        validateNumericField(
          modernizationCost,
          1,
          100000000,
          'propertyDetails.modernizationCost',
          true
        )
      }
    }
  }

  const handleChangeModernizationCostDescription = (modernizationCostDescription: string) => {
    if (modernizationCostDescription.length === 0) {
      dispatch(removeFormField('propertyDetails.modernizationCostDescription'))
      return
    }
    dispatch(
      setFormField({
        key: 'propertyDetails.modernizationCostDescription',
        value: modernizationCostDescription,
      })
    )
  }

  const onBackClick = () => {
    setCurrentTab((tab) => tab - 1)
  }

  const onContinueClick = () => {
    setShowValidation(true)
    if (validationErrors.length === 0) {
      setCurrentTab((tab) => tab + 1)
    }
  }

  return (
    <>
      <StyledContainer>
        <StyledRowContainer>
          <StyledColContainer>
            <StyledCol>
              <RadioGroupButtons
                options={modernizationMethods}
                label={t('newAddressModal.modernizationTab.modernizationMethod')}
                onChange={(event) => handleChangeModernizationMethod(event.target.value)}
                value={addressFormData.addressData?.propertyDetails?.modernizationMethod}
              />
            </StyledCol>
          </StyledColContainer>
        </StyledRowContainer>
        <StyledColContainer>
          <StyledCol>
            <Input
              disabled={
                addressFormData.addressData?.propertyDetails?.modernizationMethod ===
                ModernizationMethodType.AUTOMATIC
              }
              value={addressFormData.addressData?.propertyDetails?.refurbishmentYear || ''}
              onChange={(event) => handleChangeRefurbishmentYear(event.target.value)}
              onBlur={(event) => handleChangeRefurbishmentYear(event.target.value, true)}
              onKeyPress={(event) => handleNumberInput(event)}
              defaultValue={addressFormData.addressData?.propertyDetails?.refurbishmentYear || ''}
              label={`${t('newAddressModal.modernizationTab.refurbishmentYear')}`}
              helperText={`${addressFormData.addressData?.propertyDetails?.constructionYear} – ${t(
                'common.today'
              )}`}
            />
          </StyledCol>
        </StyledColContainer>
        <StyledLine />
        <>
          {addressFormData.addressData?.propertyDetails?.modernizationMethod ===
            ModernizationMethodType.SIMPLE && <ValueAddModernizationSlider />}
          {addressFormData.addressData?.propertyDetails?.modernizationMethod ===
            ModernizationMethodType.DETAILED && (
            <ValueModernizationMatrixForm
              showValidation={showValidation}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
            />
          )}
          {addressFormData.addressData?.propertyDetails?.modernizationMethod ===
            ModernizationMethodType.COSTS && (
            <>
              <StyledRowContainer>
                <StyledColContainer>
                  <StyledCol
                    validationError={validate(
                      'modernizationCost',
                      addressFormData.addressData?.propertyDetails?.modernizationCost
                    )}
                  >
                    <Input
                      value={addressFormData.addressData?.propertyDetails?.modernizationCost || ''}
                      onChange={(event) => handleChangeModernizationCost(event.target.value)}
                      onBlur={(event) => handleChangeModernizationCost(event.target.value, true)}
                      onKeyPress={(event) => handleNumberInput(event, true)}
                      defaultValue={
                        addressFormData.addressData?.propertyDetails?.modernizationCost || ''
                      }
                      label={`${t('newAddressModal.modernizationTab.modernizationCost')}*`}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" style={{ color: '#92969a' }}>
                            €
                          </InputAdornment>
                        ),
                      }}
                    />
                  </StyledCol>
                </StyledColContainer>
                <TextArea
                  value={
                    addressFormData.addressData?.propertyDetails?.modernizationCostDescription || ''
                  }
                  onChange={(event) => handleChangeModernizationCostDescription(event.target.value)}
                  maxLength={500}
                  label={t('newAddressModal.modernizationTab.modernizationCost.description')}
                />
              </StyledRowContainer>
              <StyledCostsDescription>
                {t('newAddressModal.modernizationTab.modernizationCost.information')}
              </StyledCostsDescription>
            </>
          )}
          {addressFormData.addressData?.propertyDetails?.modernizationMethod ===
            ModernizationMethodType.AUTOMATIC && (
            <StyledInfoContainer>
              <StyledInfoIcon>
                <InfoIcon />
              </StyledInfoIcon>
              {t('newAddressModal.modernizationTab.modernizationMethod.automatic.description')}
            </StyledInfoContainer>
          )}
        </>
      </StyledContainer>
      <StyledContinueButtonContainer>
        <Button kind="primary" onClick={() => onBackClick()}>
          {t('common.backButton')}
        </Button>
        <Button kind="primary" onClick={() => onContinueClick()}>
          {t('common.continueButton')}
        </Button>
      </StyledContinueButtonContainer>
    </>
  )
}

export default ValueAddModernizationForm
